import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sessionsApi } from '../lib/api';

export default function BlueprintView() {
  const { id } = useParams<{ id: string }>();

  const { data: blueprint, isLoading, error } = useQuery({
    queryKey: ['blueprint', id],
    queryFn: () => sessionsApi.getBlueprint(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-theme-secondary">読み込み中...</div>
      </div>
    );
  }

  if (error || !blueprint) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-theme-card rounded-xl p-8 border border-theme text-center transition-colors duration-300">
          <p className="text-theme-secondary mb-4">設計書がまだ生成されていません</p>
          <Link
            to={`/sessions/${id}`}
            className="text-accent hover:underline"
          >
            セッション詳細へ戻る
          </Link>
        </div>
      </div>
    );
  }

  const sections = [
    { key: 'summary', title: '概要', icon: '📝' },
    { key: 'goals', title: '目的・目標', icon: '🎯' },
    { key: 'features', title: '機能一覧', icon: '⚙️' },
    { key: 'dataModel', title: 'データモデル', icon: '🗄️' },
    { key: 'userFlow', title: 'ユーザーフロー', icon: '🔄' },
    { key: 'uiDesign', title: 'UI設計', icon: '🎨' },
    { key: 'techStack', title: '技術スタック', icon: '🛠️' },
    { key: 'timeline', title: 'スケジュール', icon: '📅' },
    { key: 'risks', title: 'リスク・課題', icon: '⚠️' },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <Link
            to={`/sessions/${id}`}
            className="text-sm text-theme-secondary hover:text-theme-primary mb-2 inline-block"
          >
            ← セッション詳細
          </Link>
          <h1 className="text-2xl font-bold text-theme-primary">設計書</h1>
          <p className="text-theme-secondary mt-1">{blueprint.title}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 border border-theme text-theme-secondary rounded-lg hover:bg-theme-hover transition-colors"
          >
            印刷 / PDF
          </button>
        </div>
      </div>

      {/* メタ情報 */}
      <div className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-theme-secondary">バージョン</p>
            <p className="text-theme-primary font-medium">{blueprint.version}</p>
          </div>
          <div>
            <p className="text-theme-secondary">ステータス</p>
            <p className="text-theme-primary font-medium">
              {blueprint.status === 'final' ? '確定' : blueprint.status === 'review' ? 'レビュー中' : 'ドラフト'}
            </p>
          </div>
          <div>
            <p className="text-theme-secondary">作成日</p>
            <p className="text-theme-primary font-medium">
              {new Date(blueprint.createdAt).toLocaleDateString('ja-JP')}
            </p>
          </div>
          <div>
            <p className="text-theme-secondary">更新日</p>
            <p className="text-theme-primary font-medium">
              {new Date(blueprint.updatedAt).toLocaleDateString('ja-JP')}
            </p>
          </div>
        </div>
      </div>

      {/* コンテンツセクション */}
      {sections.map((section) => {
        const content = blueprint.content?.[section.key];
        if (!content) return null;

        return (
          <div
            key={section.key}
            className="bg-theme-card rounded-xl p-6 border border-theme transition-colors duration-300"
          >
            <h2 className="text-lg font-bold text-theme-primary mb-4 flex items-center gap-2">
              <span>{section.icon}</span>
              <span>{section.title}</span>
            </h2>
            <div className="prose prose-invert max-w-none">
              {typeof content === 'string' ? (
                <p className="text-theme-primary whitespace-pre-wrap">{content}</p>
              ) : Array.isArray(content) ? (
                <ul className="list-disc list-inside space-y-2">
                  {content.map((item, i) => (
                    <li key={i} className="text-theme-primary">{String(item)}</li>
                  ))}
                </ul>
              ) : (
                <pre className="bg-theme-hover p-4 rounded-lg overflow-x-auto text-theme-primary text-sm">
                  {JSON.stringify(content, null, 2)}
                </pre>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
